import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import { Languages } from "../../../../../../../models/languages";
import { Filters, Searches, Sorts } from "../../../../../../../models/shipeezi.table";
import { Subcontractor } from "../../../../../../../models/subcontractor";
import FleetService from "../../../../../../../services/fleet.service";
import LanguagesService from "../../../../../../../services/languages.service";
import MembersService from "../../../../../../../services/members.service";
import SubcontractorService from "../../../../../../../services/subcontractor.service";
import { LanguagesUsage, Status } from "../../../../../../../util/enums";
import {
  FleetKeys,
  LanguageKeys,
  MemberKeys,
  SubcontractorKeys,
} from "../../../../../../../util/queries";

type CallbackSuccess = (data: any) => void;

type CreateSubcontractor = {
  data: Subcontractor;
};
type UpdateSubcontractor = {
  id: Subcontractor["id"];
  data: Partial<Subcontractor>;
};

type SaveSubcontractorProps = {
  id: number;
  data: Subcontractor;
};

const useSubcontractorActions = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const onBackToSubcontractorsTab = () => {
    const state = history.location.state as any || {};

    history?.push({
      pathname: PRIVATE_ROUTES.SETTINGS,
      state: {
        tabKey: "resources",
        subTabKey: "Subcontractors",
        tab: translate("resources"),
        tabResource: translate("subcontractors"),
      },
    });
  };

  const getAllSubcontractors = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    sorting?: Sorts
  ) => {
    const includes = [
      { association: "members", attributes: ["id"] },
      { association: "fleets", attributes: ["id"] },
      { association: "businessLocation", attributes: ["id", "longAddress", "addressLine1"] },
    ];

    return useQuery({
      queryKey: SubcontractorKeys.getAll([page, pageSize, filters, searches, sorting, includes]),
      queryFn: async () => {
        const response = await SubcontractorService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy: sorting
            ? JSON.stringify([...sorting, ["createdAt", "desc"]])
            : JSON.stringify([["createdAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          ...filters,
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const countSubcontractors = (filters?: Filters, searches?: Searches) => {
    return useQuery({
      queryKey: SubcontractorKeys.count([filters, searches]),
      queryFn: async () => {
        const response = await SubcontractorService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const useCreateSubcontractor = (callbackSuccess?: CallbackSuccess) => {
    return useMutation({
      mutationKey: SubcontractorKeys.create(),
      mutationFn: (variables: CreateSubcontractor) =>
        SubcontractorService.create({ ...variables.data, companyId: currentUser?.companyId }),
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: [SubcontractorKeys.all] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));

        if (callbackSuccess) callbackSuccess(response.data);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useUpdateSubcontractor = (callbackSuccess?: CallbackSuccess) => {
    return useMutation({
      mutationKey: SubcontractorKeys.update(),
      mutationFn: ({ id, data }: UpdateSubcontractor) =>
        SubcontractorService.update(id, data as Subcontractor),
      onSuccess: (response, variables) => {
        queryClient.invalidateQueries({ queryKey: [SubcontractorKeys.all] });

        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.subcontractor",
        });

        if (callbackSuccess) callbackSuccess(variables.data);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const getAllMembersToSubcontractor = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    const includes = [
      {
        association: "userCompany",
        include: [{ association: "role", attributes: ["id", "name"] }],
      },
      {
        association: "location",
        attributes: ["id", "country", "longAddress"],
      },
      {
        association: "branches",
        required: false,
        attributes: ["id", "name"],
        where: { id: currentUser?.branches?.map((branch) => branch.id) },
      }
    ];

    return useQuery({
      queryKey: MemberKeys.getAll(["subcontractors", page, pageSize, filters, searches, includes]),
      queryFn: async () => {
        const response = await MembersService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          subcontractorId: null, // found a way to getAll using null as value
          sortBy: JSON.stringify([["createdAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          status: Status.ACTIVE,
          ...filters,
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const countMembersToSubcontractor = (filters?: Filters, searches?: Searches) => {

    const includes = [
      {
        association: "branches",
        required: false,
        attributes: ["id", "name"],
        where: { id: currentUser?.branches?.map((branch) => branch.id) },
      }
    ];

    return useQuery({
      queryKey: MemberKeys.count([filters, searches]),
      queryFn: async () => {
        const response = await MembersService.count({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const getSubcontractorById = (id: Subcontractor["id"] | undefined) => {
    const includes = [
      { association: "businessLocation" },
      { association: "contactLocation" },
      { association: "billingLocation" },
      {
        association: "members",
        include: [
          {
            association: "userCompany",
            include: [{ association: "role", attributes: ["id", "name"] }],
          },
          {
            association: "location",
            attributes: ["id", "country", "longAddress"],
          },
        ],
      },
      { association: "fleets" },
    ];

    return useQuery({
      queryKey: SubcontractorKeys.getById(id),
      queryFn: async () => {
        if (!id) return null;

        const response = await SubcontractorService.getById(id, {
          include: JSON.stringify(includes),
        });

        if (response && response.data) return response.data;

        return null;
      },
      enabled: !!id && !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    });
  };

  const useSaveSubcontractor = (callback: () => void) => {
    return useMutation({
      mutationKey: SubcontractorKeys.update(),
      mutationFn: (values: SaveSubcontractorProps) =>
        SubcontractorService.update(values.id, values.data),
      onSuccess(_, variables) {
        queryClient.invalidateQueries({ queryKey: [SubcontractorKeys.getAll] });
        queryClient.invalidateQueries({ queryKey: [SubcontractorKeys.all, variables.id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        callback();
      },
    });
  };

  const getLanguagesAvailable = () => {
    return useQuery({
      queryKey: LanguageKeys.getAll(["subcontractors"]),
      queryFn: async () => {
        const response = await LanguagesService.getAll({
          companyId: currentUser?.companyId,
          usage: LanguagesUsage.WEB,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "asc"]]),
        });

        if (response && response.data && Array.isArray(response.data)) {
          return response.data as Languages[];
        }

        return [] as Languages[];
      },
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    });
  };

  const getAllEquipmentsForSubcontractor = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    const includes = [
      {
        association: "fleetType",
      },
      {
        association: "branches",
        required: false,
        attributes: ["id", "name"],
        where: { id: currentUser?.branches?.map((branch) => branch.id) },
      },
    ];

    return useQuery({
      queryKey: FleetKeys.getAll(["subcontractors", page, pageSize, filters, searches, includes]),
      queryFn: async () => {
        const response = await FleetService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["createdAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify(includes),
          ...filters,
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  const getCountEquipmentsForSubcontractor = (filters?: Filters, searches?: Searches) => {
    const includes = [
      {
        association: "branches",
        required: false,
        attributes: ["id", "name"],
        where: { id: currentUser?.branches?.map((branch) => branch.id) },
      },
    ];

    return useQuery({
      queryKey: FleetKeys.count(["subcontractors", filters, searches]),
      queryFn: async () => {
        const response = await FleetService.count({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          include: JSON.stringify(includes),
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!(currentUser?.id && currentUser?.companyId),
      refetchOnWindowFocus: false,
    });
  };

  return {
    onBackToSubcontractorsTab,
    getAllSubcontractors,
    countSubcontractors,
    useCreateSubcontractor,
    useUpdateSubcontractor,
    getAllMembersToSubcontractor,
    countMembersToSubcontractor,
    getSubcontractorById,
    useSaveSubcontractor,
    getLanguagesAvailable,
    getAllEquipmentsForSubcontractor,
    getCountEquipmentsForSubcontractor,
  };
};

export { useSubcontractorActions };
